import React from "react";
import { FormikErrors, FormikProps, withFormik } from "formik";
import { Button } from "@chakra-ui/button";
import { HStack, VStack } from "@chakra-ui/layout";
import { AdminApiClient } from "../api/adminApiClient";
import { FormStack } from "../components/FormStack/FormStack";
import { CloudServiceEbetApi } from "../api/ebetApiModels";
import { TextField } from "../components/fields/TextField/TextField";
import { IntegerField } from "../components/fields/IntegerField/IntegerField";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { AdminApiEnumConfigurations } from "../enums/AdminApiEnumConfigurations";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";

interface IntegrationEbetClientConfigurationFormValues
  extends CloudServiceEbetApi.ClientConfiguration {}

interface IntegrationEbetClientConfigurationFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  integrationId: string;
  configuration: { [key: string]: unknown } | null | undefined;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  onFailure?: (message?: string) => void;
}

const InnerForm = ({
  isSubmitting,
  submitForm,
  isValid,
  onCancel,
}: IntegrationEbetClientConfigurationFormProps &
  FormikProps<IntegrationEbetClientConfigurationFormValues>) => {
  const { data: timeZoneOptions, isLoading: timeZoneOptionsLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(AdminApiEnumConfigurations.TimeZone),
      []
    );

  return (
    <FormStack>
      <VStack w="100%" maxW="1200px" alignItems="flex-start" pb={10}>
        <TextField
          horizontal
          label="Server Tcp Address"
          name="ServerTcpAddress"
        />

        <TextField horizontal label="Server Address" name="ServerAddress" />

        <IntegerField horizontal label="Port" name="Port" />

        <IntegerField
          horizontal
          label="Timeout Seconds"
          name="TimeoutSeconds"
        />

        <ChakraReactSelectField
          horizontal
          label="Time Zone Id"
          name="TimeZoneId"
          selectedOptionColor="blue"
          isLoading={timeZoneOptionsLoading}
          options={
            timeZoneOptions?.map((timeZone) => {
              return {
                label: timeZone.Value!,
                value: timeZone.Key!,
              };
            }) || []
          }
        />

        <TextField
          horizontal
          label="Key Vault Service Account Uid"
          name="KeyVaultServiceAccountUid"
        />
      </VStack>

      <HStack width="100%" justifyContent="start" spacing="3" pt="4">
        <Button
          colorScheme="cherryButton"
          type="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting || !isValid}
          onClick={submitForm}
        >
          Save
        </Button>
        <Button disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    </FormStack>
  );
};

export const IntegrationEbetClientConfigurationForm = withFormik<
  IntegrationEbetClientConfigurationFormProps,
  IntegrationEbetClientConfigurationFormValues
>({
  mapPropsToValues: ({ configuration }) => {
    return configuration as CloudServiceEbetApi.ClientConfiguration;
  },
  handleSubmit: async (values, { props }) => {
    let result = await props.apiClient.updateIntegrationConfiguration(
      props.businessId,
      props.integrationId,
      // `updateIntegrationConfiguration` is typed as accepting a JSON string,
      // but (as in IntegrationConfigurationForm) the runtime client actually
      // PUTs whatever value it is given as the request body.
      values as any
    );

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess();
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,
  validate: (values) => {
    let errors: FormikErrors<IntegrationEbetClientConfigurationFormValues> = {};

    return errors;
  },
})(InnerForm);
